<template>
  <section class="section">
    <div class="container">

      <canvas ref="mainCanvas"
              style="background: transparent; position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 50; pointer-events: none;"></canvas>

      <section v-if="!payoutsError">
        <CreateRecipient v-model="recipientAddress"></CreateRecipient>

        <transition name="scale-height">
          <div v-if="isAddressValid && !shouldShowProgress">
            <div class="is-size-3" style="margin-top: 30px; margin-bottom: 20px">
              Solve the captcha below to obtain your NANO
            </div>
            <recaptcha class="" v-on:gRecaptchaResponse="onGRecaptchaResponse"></recaptcha>
          </div>
        </transition>

        <transition name="scale-height">
          <div class="" style="margin-top: 20px; margin-bottom: 20px;" v-if="shouldShowProgress">
            <h1 class="is-size-2" style="margin-bottom: 20px;">{{ progressTitle }}</h1>

            <div style="height: 80px; position: relative; text-align: center;">
              <b-loading :active="true" :is-full-page="false"></b-loading>
              <div>
            <span style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);">
              <span :class="sentCheckmarkClass" class="icon has-text-light ease-out-back">
                <i class="fas fa-check fa-2x" style="background-color: #23d160; padding: 20px; border-radius: 50%"></i>
              </span>
            </span>
              </div>
            </div>

          </div>
        </transition>

        <transition name="scale-height">
          <div v-if="!!completedSessionData">

            <div class="is-size-2">
              Congratulations!
            </div>
            <div :class="completedSessionTextClass" class="is-size-3">
              {{ completedSessionTextLine1 }}
            </div>
            <div :class="completedSessionTextClass" class="is-size-4">
              <a :href="getBlockExplorerURL" target="_blank">
                {{ getBlockText }}
              </a>
            </div>

            <button @click="getMoreNANOClicked" class="button is-rounded is-primary is-large" style="margin-top: 1rem"
                    type="button">
              Get More NANO!
            </button>

          </div>
        </transition>

      </section>

      <div style="height: 2rem"></div>

      <!-- Basic Ad Unit 1 -->
      <!--<ins class="adsbygoogle"-->
      <!--style="display:block"-->
      <!--data-ad-client="ca-pub-5014469443290772"-->
      <!--data-ad-slot="3804302859"-->
      <!--data-ad-format="auto"-->
      <!--data-full-width-responsive="true"></ins>-->

      <section style="font-size: 2em;">
        A Lil NANO is on hiatus for the time being.
        <br/>
        Follow
        <a href="https://twitter.com/pryszkie" style="color: #167df0" target="_blank">
          @pryszkie&nbsp;
          <span class="icon has-text-info is-small">
            <i class="fab fa-twitter"></i>
          </span>
        </a>
        &nbsp;for updates.
      </section>

      <hr style="background-color: #555;" />

      <!-- payouts -->
      <div class="">
        <h1 class="is-size-2">
          Payouts
        </h1>
        <div v-if="payouts">
          <div class="columns is-size-3" style="margin-bottom: 0">
            <div class="column is-mobile is-4"></div>
            <div class="column is-mobile is-2">Chance</div>
            <div class="column is-mobile is-2">
              Payout
              <span>&nbsp;</span>
              <b-tooltip animated label="See NANO unit explanation">
                <a href="https://github.com/nanocurrency/nano-node/wiki/Distribution,-Mining-and-Units" target="_blank">
                <span class="icon has-text-info is-small">
                  <i class="fas fa-info-circle is-small"></i>
                </span>
                </a>
              </b-tooltip>
            </div>
            <div class="column is-mobile is-4"></div>
          </div>
          <div style="width: 40%; text-align: center; height: 2px; background-color: #222; margin: 0.1rem auto 1rem;"></div>
          <div v-bind:key="payout.amount" v-for="(payout, index) in payouts">
            <div class="columns is-size-4">
              <div class="column is-mobile is-4"></div>
              <div :class="payoutTextClasses[index]" class="column is-mobile is-2">{{ formatChance(payout.chance) }}
              </div>
              <div :class="payoutTextClasses[index]" class="column is-mobile is-2">{{ formatAmount(payout.amount) }}
              </div>
              <div class="column is-mobile is-4"></div>
            </div>
          </div>
          <br>
          <h1 class="is-size-2">
            <b-tooltip label="Expected Value on Wikipedia"><a
                    href="https://en.wikipedia.org/wiki/Expected_value#Finite_case" target="_blank">Average Expected
              Payout</a></b-tooltip>
            :
          </h1>
          <div class="is-size-2 has-text-info">
            ~{{ expectedPayoutText }}
          </div>
        </div>
        <div v-else>
          <div v-if="payoutsError">
            <h2 class="is-size-3">
              There was an error getting payout rates. Trying again...
            </h2>
          </div>
          <div style="height: 80px; position: relative; text-align: center;">
            <b-loading :active="true" :is-full-page="false" style="background-color: #080808"></b-loading>
          </div>
        </div>
      </div>

      <div class="">
        <div class="is-size-2" style="">
          Donations to the faucet are greatly appreciated! And you can use this address as your representative to help
          out the NANO network:
          <div>
            <code class="code-color dark-background is-size-5" style="overflow-wrap: break-word;">{{ faucetAddress
              }}</code>
          </div>
          <div>
            <canvas ref="addressqrcode" style=""></canvas>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script>
  import BackendAPI from '../lib/BackendAPI'
  import SessionState from '../lib/SessionState'
  import Util from '../lib/Util'
  import {Power0, Power2, Power4, TweenLite} from 'gsap/all'
  import Recaptcha from "../components/Recaptcha"
  import BLoading from "buefy/src/components/loading/Loading";
  import BTooltip from "buefy/src/components/tooltip/Tooltip";
  import CreateRecipient from "../components/CreateRecipient"
  import _ from 'underscore'
  import QRCode from 'qrcode'

  const defaultWaitTime = 10000 // milliseconds
  const tau = 2 * Math.PI

  const payoutTextClasses = [
    'level-0-text',
    'level-1-text',
    'level-2-text',
  ]

  export default {
    name: "Home",
    data() {
      return {
        recipientAddress: '',
        waitTime: defaultWaitTime,
        progressUpdateTimer: null,
        shouldShowProgress: false,
        sentCheckmarkClass: 'fade-enter',
        payouts: null,
        payoutsError: null,
        navVisible: false,
        faucetAddress: 'nano_1r98z5i14ztss597ajy3y9no5kpm1bm3uj1gam4gstjhhy3othmus1mamxyz',
        completedSessionData: null,
        payoutTextClasses,
      }
    },
    components: {
      BTooltip,
      BLoading,
      Recaptcha,
      CreateRecipient
    },
    computed: {
      isAddressValid() {
        const isValid = Util.isAddressValid({address: this.recipientAddress})
        if (!isValid) {
          return false
        }
        this.saveRecipientAddress()
        return true
      },
      expectedPayoutText() {
        if (!this.payouts) {
          return ''
        }
        let expectedPayoutValue = this.payouts.map(payout => payout.amount * payout.chance).reduce((a, b) => a + b)
        return this.formatAmount(expectedPayoutValue)
      },
      progressTitle() {
        if (this.completedSessionData) {
          return `NANO Sent!`
        }
        if (this.waitTime <= 0) {
          return 'Sending NANO...'
        }
        return `Waiting ${(this.waitTime / 1000).toLocaleString(undefined, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        })} seconds`
      },
      completedSessionTextLine1() {
        if (!this.completedSessionData) {
          return ''
        }
        return `You earned ${this.formatAmount(this.completedSessionData.payout.amount)}`
      },
      completedSessionTextClass() {
        if (!this.completedSessionData) {
          return ''
        }
        return payoutTextClasses[this.completedSessionData.payout.level]
      },
      getBlockExplorerURL() {
        if (!this.completedSessionData) {
          return ''
        }
        return `https://nanocrawler.prizzventuresllc.com/#/explorer/block/${this.completedSessionData.block}`
      },
      getBlockText() {
        if (!this.completedSessionData) {
          return ''
        }
        return `Block: ${this.completedSessionData.block.slice(0, 10)}...`
      }
    },
    methods: {
      getMoreNANOClicked() {
        this.resetState()
      },
      formatChance(chance) {
        return `${chance * 100}%`
      },
      formatAmount(amount) {
        let unit
        let formattedAmount = amount
        if (amount >= 1e30) {
          unit = 'NANO'
          formattedAmount = amount / 1e30
        } else if (amount >= 1e27) {
          unit = 'mNANO'
          formattedAmount = amount / 1e27
        } else if (amount >= 1e24) {
          unit = 'μNANO'
          formattedAmount = amount / 1e24
        } else {
          unit = 'nNANO'
          formattedAmount = amount / 1e21
        }
        return `${Math.round(formattedAmount)} ${unit}`
      },
      shouldShowSentCheckmark(shouldShow) {
        this.sentCheckmarkClass = shouldShow ? 'scale-up' : 'fade-enter'
      },
      async completeSession() {
        try {
          const sessionResponse = await BackendAPI.completeSession()
          if (!sessionResponse) {
            throw 0
          }
          this.sessionDidComplete({sessionResponse})
        } catch (e) {
          this.showError()
          console.error('An error occurred while sending NANO')
        }
      },
      sessionDidComplete({sessionResponse}) {
        this.completedSessionData = sessionResponse
        this.shouldShowSentCheckmark(true)
        this.displayConfetti({payoutLevel: sessionResponse.payout.level})
      },
      showError() {
        this.$toast.open({
          type: 'is-danger',
          duration: 5000,
          message: 'An error occurred. Please try again.'
        })
        this.resetState()
      },
      resetState() {
        this.shouldShowProgress = false
        this.completedSessionData = null
        this.recipientAddress = ''
        this.shouldShowSentCheckmark(false)
        this.loadRecipientAddress()
      },
      displayConfetti({payoutLevel}) {
        const canvas = this.$refs.mainCanvas
        const devicePixelRatio = window.devicePixelRatio || 1
        const context = canvas.getContext('2d')
        context.scale(devicePixelRatio, devicePixelRatio)
        canvas.width = devicePixelRatio * window.innerWidth
        canvas.height = devicePixelRatio * window.innerHeight

        const payoutColorFunctions = [
          () => {
            return `rgb(${_.random(10, 100)}, ${_.random(10, 200)}, ${_.random(150, 250)})`
          },
          () => {
            return `rgb(${_.random(10, 100)}, ${_.random(150, 250)}, ${_.random(10, 200)})`
          },
          () => {
            return `rgb(${_.random(200, 240)}, ${_.random(140, 230)}, ${_.random(10, 70)})`
          },
        ]

        const payoutShoots = [
          50, 70, 100
        ]

        let confettiShoots = payoutShoots[payoutLevel]
        const colorFunction = payoutColorFunctions[payoutLevel]

        const confettiSprites = new Set()
        const sparkleGlitterSprites = new Set()

        setTimeout(() => {
          function generateConfettiSprite() {
            return {
              x: Math.random() * canvas.width,
              y: -200 * Math.random(),
              lineWidth: _.random(20, 60),
              lineLength: _.random(40, 60),
              rotation: _.random(0, tau),
              color: colorFunction()
            }
          }

          function generateSparkleGlitterSprite() {
            return {
              x: Math.random() * canvas.width,
              y: -200 * Math.random(),
              lineWidth: _.random(3, 8),
              lineLength: _.random(4, 10),
              rotation: _.random(0, tau),
              randomColorOffset: Math.random() * tau,
              getColor() {
                return Math.sin(this.y * 0.01 + this.randomColorOffset) > 0.98 ? `rgb(255, 252, 186)` : `rgb(68, 67, 46)`
              }
            }
          }

          function generateGlitterSprite() {
            return {
              x: Math.random() * canvas.width,
              y: -200 * Math.random(),
              opacity: 0,
            }
          }

          function addConfettiParticles({count}) {
            for (let i = 0; i < count; ++i) {
              const confettiSprite = generateConfettiSprite()
              confettiSprites.add(confettiSprite)
              tweenConfettiParticle(confettiSprite)
            }

            for (let i = 0; i < count * 3; ++i) {
              const sparkleGlitterSprite = generateSparkleGlitterSprite()
              sparkleGlitterSprites.add(sparkleGlitterSprite)
              tweenSparkleGlitter(sparkleGlitterSprite)
            }
          }

          function tweenConfettiParticle(sprite) {
            const duration = 3
            TweenLite.to(sprite, duration, {
              y: canvas.height * 1.1 + 200 * Math.random(),
              ease: Power2.easeIn,
              onComplete: () => {
                confettiSprites.delete(sprite)
              }
            })

            TweenLite.to(sprite, duration, {
              x: sprite.x + _.random(-2000, 2000),
              rotation: _.random(-6 * tau, 7 * tau),
              ease: Power0.easeNone,
              onComplete: () => {
                confettiSprites.delete(sprite)
              }
            })
          }

          function tweenSparkleGlitter(sprite) {
            const duration = 3
            TweenLite.to(sprite, duration, {
              y: canvas.height * 1.1 + 200 * Math.random(),
              ease: Power2.easeIn,
              onComplete: () => {
                confettiSprites.delete(sprite)
              }
            })

            TweenLite.to(sprite, duration, {
              x: sprite.x + _.random(-2000, 2000),
              rotation: _.random(-6 * tau, 7 * tau),
              ease: Power0.easeNone,
              onComplete: () => {
                confettiSprites.delete(sprite)
              }
            })
          }

          function tweenGlitter(glitterSprite) {
            const duration = 3
            TweenLite.to(glitterSprite, duration, {
              y: canvas.height * 1.1 + 200 * Math.random(),
              ease: Power2.easeIn,
              onComplete: () => {
                confettiSprites.delete(glitterSprite)
              }
            })

            TweenLite.to(glitterSprite, duration, {
              x: glitterSprite.x + _.random(-2000, 2000),
              ease: Power0.easeNone,
              onComplete: () => {
                confettiSprites.delete(glitterSprite)
              }
            })

            const glitterSparkleDuration = 0.3
            const glitterSparkleDelay = Math.random() * duration

            TweenLite.fromTo(glitterSprite, glitterSparkleDuration, {
              opacity: 0,
            }, {
              delay: glitterSparkleDelay,
              opacity: 1,
              ease: Power4.easeIn,
            })

            TweenLite.to(glitterSprite, glitterSparkleDuration, {
              delay: glitterSparkleDelay + glitterSparkleDuration,
              ease: Power4.easeOut,
              opacity: 0
            })
          }

          function shootConfetti() {
            if (confettiShoots <= 0) {
              return
            }
            --confettiShoots
            setTimeout(() => {
              addConfettiParticles({count: 3})
              shootConfetti()
            }, 30)
          }

          shootConfetti()
          TweenLite.ticker.addEventListener('tick', render)

          function drawConfetti() {
            confettiSprites.forEach(sprite => {
              context.beginPath()
              context.lineWidth = sprite.lineWidth
              context.strokeStyle = sprite.color
              context.translate(sprite.x, sprite.y)
              context.rotate(sprite.rotation)
              context.moveTo(-sprite.lineLength / 2, 0)
              context.lineTo(sprite.lineLength / 2, 0)
              context.stroke()
              context.setTransform(1, 0, 0, 1, 0, 0) // identity
            })
          }

          function drawSparkleGlitter() {
            sparkleGlitterSprites.forEach(sprite => {
              context.beginPath()
              context.lineWidth = sprite.lineWidth
              context.strokeStyle = sprite.getColor()
              context.translate(sprite.x, sprite.y)
              context.rotate(sprite.rotation)
              context.moveTo(-sprite.lineLength / 2, 0)
              context.lineTo(sprite.lineLength / 2, 0)
              context.stroke()
              context.setTransform(1, 0, 0, 1, 0, 0) // identity
            })
          }

          function render() {
            context.clearRect(0, 0, canvas.width, canvas.height)

            drawConfetti()
            drawSparkleGlitter()
          }
        }, 20)
      },
      saveRecipientAddress() {
        localStorage.setItem('recipientAddress', this.recipientAddress)
      },
      async onGRecaptchaResponse(gRecaptchaResponse) {
        const firstCaptchaResponse = await BackendAPI.postFirstCaptcha({
          gRecaptchaResponse,
          nanoAddress: this.recipientAddress
        })
        if (!firstCaptchaResponse) {
          return this.showError()
        }
        switch (firstCaptchaResponse.state) {
          case SessionState.running:
            this.startProgressUpdateTimer({waitTime: firstCaptchaResponse.timeRemaining})
            break
          case SessionState.ready:
            this.showError()
            break
          case SessionState.banned:
            this.showError()
            break
          default:
            this.showError()
            break
        }
      },
      startProgressUpdateTimer({waitTime}) {
        if (!isFinite(waitTime)) {
          return
        }
        const startingWaitTime = waitTime
        this.waitTime = waitTime
        this.shouldShowProgress = true
        const progressStartDate = Date.now()
        this.shouldShowSentCheckmark(false)
        this.progressUpdateTimer = setInterval(() => {
          const msSinceStart = Date.now() - progressStartDate
          let waitPercent = msSinceStart / startingWaitTime
          if (waitPercent >= 1) {
            this.completeSession()
            clearInterval(this.progressUpdateTimer)
          }
          this.waitTime = startingWaitTime - msSinceStart
        }, 1000 / 30)
      },
      async fetchPayouts() {
        try {
          this.payouts = await BackendAPI.getPayouts()
          this.payoutsError = null
        } catch (error) {
          this.payoutsError = error
          setTimeout(() => {
            this.fetchPayouts()
          }, 7007)
        }
      },
      loadRecipientAddress() {
        setTimeout(() => {
          const currentRecipientAddress = localStorage.getItem('recipientAddress')
          if (currentRecipientAddress) {
            this.recipientAddress = currentRecipientAddress
          }
        }, 10)
      },
    },
    mounted() {
      BackendAPI.getSessionState().then(result => {
        if (!result || !result.state) {
          return
        }
        switch (result.state) {
          case SessionState.banned:
            return
          case SessionState.ready:
            return
          case SessionState.running:
            this.startProgressUpdateTimer({waitTime: result.timeRemaining})
            return
          default:
            return
        }
      }).catch(() => {
      })
      this.loadRecipientAddress()
      this.fetchPayouts()
      QRCode.toCanvas(this.$refs.addressqrcode, this.faucetAddress)
      setInterval(() => {
        this.displayConfetti({payoutLevel: 0})
      }, 7000)
    }
  }
</script>

<style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 1.0s;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .scale-height-enter-active, .scale-height-leave-active {
    transition: all 1.0s;
  }

  .scale-height-enter, .scale-height-leave-to {
    opacity: 0;
    max-height: 0;
  }

  .scale-height-enter-to, .scale-height-leave {
    max-height: 1000px;
  }

  .scale-up {
    transform: scale(1.2);
  }

  .ease-out-back {
    transition: all 300ms cubic-bezier(.17, .89, .26, 1.76);
  }

  .dark-background {
    background-color: #080808;
  }

  .code-color {
    color: darkcyan;
  }

  .loading-overlay .loading-background {
    background-color: #080808;
  }

  .loading-overlay > div.loading-background {
    background-color: #080808;
  }

  .level-0-text {
    color: rebeccapurple;
  }

  .level-1-text {
    color: forestgreen;
  }

  .level-2-text {
    color: gold;
  }
</style>
